import { HttpErrorResponse } from '@angular/common/http';
import { computed, Injector, linkedSignal, Signal, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { OpenSearchRequestFilter } from '@libs/shared/domain/aggregations/opensearch/open-search-query.schema';
import { OpenSearchPage, OpenSearchQueryResult, OpenSearchSimpleSort } from '@shared/providers/opensearch-api';
import { OpenSearchClient } from '@shared/providers/opensearch-client';
import { isEqual } from 'lodash';
import { map, Observable, of, switchMap } from 'rxjs';

export const DEFAULT_PAGE_SIZE = 20;

export type DataViewSearchFn<T> = (
  filter: OpenSearchRequestFilter[],
  page: OpenSearchPage,
  sort: OpenSearchSimpleSort,
  fields?: string[],
) => Observable<OpenSearchQueryResult<T>>;

export type DataViewCountFn = (filter: OpenSearchRequestFilter[]) => Observable<{ count: number }>;

export interface DataViewDataOptions<T> {
  search: DataViewSearchFn<T>;
  count?: DataViewCountFn;
  requestFilters: Signal<OpenSearchRequestFilter[]>;
  sort?: Signal<OpenSearchSimpleSort>;
  fields?: Signal<string[] | undefined>;
  pageSize?: Signal<number>;
  gate?: (filter: OpenSearchRequestFilter[], page: OpenSearchPage) => Observable<boolean>;
  mapItems?: (items: T[]) => T[];
  enabled?: Signal<boolean>;
  injector?: Injector;
}

export interface DataViewData<T> {
  rows: Signal<T[]>;
  total: Signal<number>;
  reachableTotal: Signal<number>;
  isLoading: Signal<boolean>;
  pageIndex: Signal<number>;
  isFirstLoad: Signal<boolean>;
  hasMore: Signal<boolean>;
  error: Signal<HttpErrorResponse | undefined>;
  loadMore(): void;
  reload(): void;
}

export function createDataViewData<T>(options: DataViewDataOptions<T>): DataViewData<T> {
  const sort = options.sort ?? signal<OpenSearchSimpleSort>({});
  const fields = options.fields ?? signal<string[] | undefined>(undefined);
  const pageSize = options.pageSize ?? signal(DEFAULT_PAGE_SIZE);
  const enabled = options.enabled ?? signal(true);
  const reloadTick = signal(0);

  const queryKey = computed(
    () => ({
      filters: options.requestFilters(),
      sort: sort(),
      fields: fields(),
      tick: reloadTick(),
    }),
    { equal: isEqual },
  );

  const pageIndex = linkedSignal({ source: queryKey, computation: () => 0 });
  const rows = linkedSignal<unknown, T[]>({ source: queryKey, computation: () => [] });

  const resource = rxResource({
    defaultValue: { total: 0, error: undefined as HttpErrorResponse | undefined },
    injector: options.injector,
    params: () => (enabled() ? { key: queryKey(), index: pageIndex(), size: pageSize() } : undefined),
    stream: ({ params }) => {
      const { filters, sort: sortValue, fields: fieldsValue } = params.key;
      const page: OpenSearchPage = { from: params.index * params.size, size: params.size };
      const projection = fieldsValue?.length ? fieldsValue : undefined;
      const replace = params.index === 0;

      const gated = options.gate?.(filters, page) ?? of(true);

      return gated.pipe(
        switchMap(allowed => (allowed ? options.search(filters, page, sortValue, projection) : of(emptyResult<T>()))),
        map(result => {
          if (result.error) return { total: 0, error: result.error };

          const items = options.mapItems ? options.mapItems(result.items) : result.items;
          rows.update(current => (replace ? items : [...current, ...items]));

          return { total: result.total, error: undefined };
        }),
      );
    },
  });

  const countKey = computed(() => ({ filters: options.requestFilters(), tick: reloadTick() }), { equal: isEqual });

  const count = options.count;
  const countResource = count
    ? rxResource({
        defaultValue: { count: 0 },
        injector: options.injector,
        params: () => (enabled() ? countKey() : undefined),
        stream: ({ params }) => count(params.filters),
      })
    : undefined;

  const total = computed(() => (countResource ? countResource.value().count : resource.value().total));
  const reachableTotal = computed(() => Math.min(total(), OpenSearchClient.OPEN_SEARCH_LIMIT));
  const isLoading = computed(() => resource.isLoading());
  const hasMore = computed(() => rows().length < reachableTotal());

  return {
    rows: rows.asReadonly(),
    total,
    reachableTotal,
    isLoading,
    pageIndex: pageIndex.asReadonly(),
    isFirstLoad: computed(() => isLoading() && rows().length === 0),
    hasMore,
    error: computed(() => resource.value().error),
    loadMore: () => {
      if (isLoading() || !hasMore()) return;
      pageIndex.update(index => index + 1);
    },
    reload: () => reloadTick.update(tick => tick + 1),
  };
}

function emptyResult<T>(): OpenSearchQueryResult<T> {
  return { items: [], total: 0 };
}
